import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { addToStatus, removeFromStatus, add, update, insertSorted } from './helpers';
import { initialState } from './initialState';
import { type BoardTask, type BoardTaskComment, type BoardTaskStatus } from '../../../../common/types/task';
import { tasksBoardApi } from '../../../api/tasksBoardApi';

export interface BoardTasksState {
  byId: Record<number, BoardTask>;
  idsByStatus: Record<BoardTaskStatus, number[]>;
  cursorByStatus: Record<BoardTaskStatus, number | null>;
  isFullyLoadedByStatus: Record<BoardTaskStatus, boolean>;
  countByStatus: Record<BoardTaskStatus, number>;
  isWorkTime: boolean;
}

export const tasksSlice = createSlice({
  name: 'boardTasks',
  initialState,
  reducers: {
    setColumnTasks(
      state,
      action: PayloadAction<{
        status: BoardTaskStatus;
        tasks: BoardTask[];
        append?: boolean;
      }>
    ) {
      const { status, tasks, append } = action.payload;

      if (!append) {
        state.cursorByStatus[status] = null;
        state.idsByStatus[status] = [];
      }

      for (const task of tasks) {
        state.byId[task.id] = task;
        addToStatus(state.idsByStatus, status, task.id, 'push');
      }
    },

    setCursor(state, action: PayloadAction<{ status: BoardTaskStatus; cursor: number | null }>) {
      const { status, cursor } = action.payload;

      state.cursorByStatus[status] = cursor;
    },

    setIsFullyLoaded(state, actions: PayloadAction<{ status: BoardTaskStatus; isFullyLoaded: boolean }>) {
      const { status, isFullyLoaded } = actions.payload;

      state.isFullyLoadedByStatus[status] = isFullyLoaded;
    },

    addTask(state, action: PayloadAction<{ task: BoardTask; skipInsert?: boolean }>) {
      const { task, skipInsert } = action.payload;

      add(state, task);

      if (!skipInsert) insertSorted(state, task);
    },

    updateTask(
      state,
      action: PayloadAction<{
        id: number;
        patch: Partial<Omit<BoardTask, 'id'>>;
      }>
    ) {
      const { id, patch } = action.payload;
      update(state, id, patch);
    },

    upsertTask(state, action: PayloadAction<{ task: BoardTask; skipInsert?: boolean }>) {
      const { task, skipInsert } = action.payload;

      const { id, ...patch } = task;

      if (state.byId[id]) {
        update(state, id, patch);
      } else {
        add(state, task);

        if (!skipInsert) insertSorted(state, task);
      }
    },

    removeTaskFromBoard(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;

      for (const status in state.idsByStatus) {
        const statusKey = status as BoardTaskStatus;

        removeFromStatus(state.idsByStatus, statusKey, id);
      }

      delete state.byId[id];
    },

    updateTaskLastComment(
      state,
      action: PayloadAction<{
        id: number;
        comment: Omit<BoardTaskComment, 'isViewed'>;
        options?: { commentsCountDelta?: number };
      }>
    ) {
      const { id, comment } = action.payload;
      const { commentsCountDelta } = action.payload.options || {};

      const task = state.byId[id];
      if (!task) return;

      const nexCommentsCount = commentsCountDelta
        ? task.commentsCount + commentsCountDelta
        : task.commentsCount;

      Object.assign(task, { lastComment: comment, commentsCount: nexCommentsCount });
    },

    changeTaskUnreadCommentsCount(state, action: PayloadAction<{ taskId: number; delta: number }>) {
      const { taskId, delta } = action.payload;

      const task = state.byId[taskId];
      if (!task) return;

      Object.assign(task, { newCommentsCount: Math.max(0, task.newCommentsCount + delta) });
    },

    changeTaskCommentsCount(state, action: PayloadAction<{ taskId: number; delta: number }>) {
      const { taskId, delta } = action.payload;

      const task = state.byId[taskId];
      if (!task) return;

      Object.assign(task, { commentsCount: task.commentsCount + delta });
    },

    setIsWorkTime(state, action: PayloadAction<boolean>) {
      state.isWorkTime = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(tasksBoardApi.endpoints.getTasksCounters.matchFulfilled, (state, action) => {
      for (const status in action.payload.data) {
        const statusKey = status as BoardTaskStatus;

        state.countByStatus[statusKey] = action.payload.data[statusKey];
      }
    });
    builder.addMatcher(tasksBoardApi.endpoints.getWorktimeStatus.matchFulfilled, (state, action) => {
      const { isWorkTime } = action.payload;

      state.isWorkTime = isWorkTime;
    });
  },
});

export const {
  setColumnTasks,
  setCursor,
  addTask,
  updateTask,
  upsertTask,
  updateTaskLastComment,
  removeTaskFromBoard,
  changeTaskUnreadCommentsCount,
  changeTaskCommentsCount,
  setIsFullyLoaded,
  setIsWorkTime,
} = tasksSlice.actions;

export const tasksReducer = tasksSlice.reducer;
